import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../../components/ui/sheet";
import { Calendar as CalendarPicker } from "../../../components/ui/calendar";
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Gauge,
  MapPin,
  Navigation,
  Route,
} from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { toast } from "react-toastify";

const DEFAULT_CENTER = [78.486671, 17.385044];
const DEBUG_DAY_PLAYBACK_FIT = process.env.NODE_ENV !== "production";
const START_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="28" fill="#22c55e" stroke="white" stroke-width="4"/>
    <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">S</text>
  </svg>
`)}`;
const END_MARKER_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="28" fill="#ef4444" stroke="white" stroke-width="4"/>
    <text x="32" y="40" font-size="22" fill="white" font-family="Arial" text-anchor="middle" font-weight="bold">E</text>
  </svg>
`)}`;

function parseTripDateLoose(dateString) {
  if (!dateString) return null;
  const s = String(dateString).trim();
  const isoLike = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(s) ? s.replace(" ", "T") : s;
  const d = new Date(isoLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateKey(dateObj) {
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(
    dateObj.getDate()
  ).padStart(2, "0")}`;
}

function parseDateKey(dateKey) {
  if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return new Date();
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatTimeAmPm(dateString) {
  if (!dateString) return "—";
  const d = parseTripDateLoose(dateString);
  if (!d) return "—";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
}

function formatDurationHuman(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh > 0 ? `${d}d ${hh}h` : `${d}d`;
}

function pointToLatLng(p) {
  if (!p || typeof p !== "object") return null;
  const lat = Number(
    p.latitude ?? p.lat ?? p?.location?.latitude ?? p?.location?.lat ?? p?.geo_location?.lat
  );
  const lng = Number(
    p.longitude ?? p.lng ?? p?.location?.longitude ?? p?.location?.lng ?? p?.geo_location?.lng
  );
  if (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  ) {
    return { lat, lng };
  }
  return null;
}

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function normalizeTripsList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.trips)) return res.trips;
  if (res.data && Array.isArray(res.data.data)) return res.data.data;
  return [];
}

function waitForMapplsSdk(maxMs = 10000) {
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      if (window.mappls?.Map && window.mappls?.Marker && window.mappls?.Polyline && window.mappls?.LatLngBounds) {
        resolve();
        return;
      }
      if (Date.now() - t0 > maxMs) {
        reject(new Error("Mappls SDK not ready"));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function fitViewport(map, path, sheetOpen, options = {}) {
  if (!map || !path?.length) return;
  if (!window.mappls?.LatLngBounds) return;
  const maxZoom = Number.isFinite(options?.maxZoom) ? options.maxZoom : 14;
  const normalized = path.filter(
    (p) =>
      p &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lng) &&
      p.lat >= -90 &&
      p.lat <= 90 &&
      p.lng >= -180 &&
      p.lng <= 180
  );
  if (!normalized.length) return;

  // Mappls fitBounds can throw on very large/complex paths.
  // Use a sampled subset for bounds calculation while drawing full polyline.
  const MAX_FIT_POINTS = 600;
  const step = Math.max(1, Math.ceil(normalized.length / MAX_FIT_POINTS));
  const fitPath = normalized.filter((_, idx) => idx % step === 0);
  if (fitPath[fitPath.length - 1] !== normalized[normalized.length - 1]) {
    fitPath.push(normalized[normalized.length - 1]);
  }
  if (DEBUG_DAY_PLAYBACK_FIT) {
    try {
      const minLat = Math.min(...fitPath.map((p) => p.lat));
      const maxLat = Math.max(...fitPath.map((p) => p.lat));
      const minLng = Math.min(...fitPath.map((p) => p.lng));
      const maxLng = Math.max(...fitPath.map((p) => p.lng));
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;

      
    } catch (err) {
    }
  }
  try {
    const bounds = new window.mappls.LatLngBounds();
    fitPath.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, { padding: 100, maxZoom, duration: 1000 });
  } catch (err) {
    // Safe fallback center/zoom so user still sees route area.
    try {
      const minLat = Math.min(...normalized.map((p) => p.lat));
      const maxLat = Math.max(...normalized.map((p) => p.lat));
      const minLng = Math.min(...normalized.map((p) => p.lng));
      const maxLng = Math.max(...normalized.map((p) => p.lng));
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      if (typeof map.setCenter === "function") map.setCenter([centerLng, centerLat]);
      if (typeof map.setZoom === "function") map.setZoom(Math.min(maxZoom, 11));
    } catch (_) {}
    return;
  }

  if (!sheetOpen || typeof map.getZoom !== "function" || typeof map.setCenter !== "function") return;
  setTimeout(() => {
    try {
      const z = map.getZoom();
      if (typeof z !== "number" || !Number.isFinite(z)) return;
      const minLat = Math.min(...fitPath.map((p) => p.lat));
      const maxLat = Math.max(...fitPath.map((p) => p.lat));
      const minLng = Math.min(...fitPath.map((p) => p.lng));
      const maxLng = Math.max(...fitPath.map((p) => p.lng));
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      const shiftPx = 120;
      const latRad = (centerLat * Math.PI) / 180;
      const mPerPx = (156543.03392 * Math.cos(latRad)) / Math.pow(2, z);
      const deltaLat = shiftPx * mPerPx * (1 / 111320);
      map.setCenter([centerLng, centerLat - deltaLat]);
    } catch (_) {}
  }, 200);
}

function formatCoord(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "—";
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function tripStartDate(tr) {
  return tr?.trip_start_time ?? tr?.startTime ?? tr?.start_time ?? tr?.created_at ?? tr?.createdAt;
}

export default function DayTripsPlaybackHistory() {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { get } = useApi();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapLoadedRef = useRef(false);
  const polylineRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const combinedPointsRef = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timelineSheetOpen, setTimelineSheetOpen] = useState(false);
  const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
  const [allTrips, setAllTrips] = useState([]);
  const [dayTrips, setDayTrips] = useState([]);
  const [segments, setSegments] = useState([]);
  const [combinedPoints, setCombinedPoints] = useState([]);
  const [expandedTrips, setExpandedTrips] = useState({});
  const initialDateKey = searchParams.get("date") || toDateKey(new Date());
  const [selectedDateKey, setSelectedDateKey] = useState(initialDateKey);

  const selectedCalendarDate = useMemo(() => parseDateKey(selectedDateKey), [selectedDateKey]);
  const mapBasePath = location.pathname.startsWith("/customer/") ? "/customer" : "/admin";

  const distanceByCalendarDay = useMemo(() => {
    const byDay = new Map();
    for (const tr of allTrips) {
      const sd = parseTripDateLoose(tripStartDate(tr));
      if (!sd) continue;
      const key = toDateKey(sd);
      const km = Number(tr?.distance_km ?? tr?.distanceKm ?? tr?.distance ?? 0);
      byDay.set(key, (byDay.get(key) || 0) + (Number.isFinite(km) ? km : 0));
    }
    return byDay;
  }, [allTrips]);

  const summary = useMemo(() => {
    if (!combinedPoints.length) return { runtimeLabel: "—", distanceLabel: "—", avgLabel: "—" };

    let km = 0;
    for (let i = 0; i < combinedPoints.length - 1; i++) {
      const a = pointToLatLng(combinedPoints[i]);
      const b = pointToLatLng(combinedPoints[i + 1]);
      if (a && b) km += getDistance(a.lat, a.lng, b.lat, b.lng);
    }

    const t0 = parseTripDateLoose(combinedPoints[0]?.point_time)?.getTime();
    const t1 = parseTripDateLoose(combinedPoints[combinedPoints.length - 1]?.point_time)?.getTime();
    const runtimeMs = Number.isFinite(t0) && Number.isFinite(t1) ? Math.max(0, t1 - t0) : null;
    const avg = dayTrips.length > 0 ? km / dayTrips.length : 0;

    return {
      runtimeLabel: formatDurationHuman(runtimeMs),
      distanceLabel: km < 0.001 ? `${(km * 1000).toFixed(0)} m` : `${km.toFixed(2)} km`,
      avgLabel: `${avg.toFixed(2)} km/trip`,
    };
  }, [combinedPoints, dayTrips]);

  const timelineItems = useMemo(() => {
    const items = [];
    segments.forEach((seg, index) => {
      items.push({
        kind: "trip",
        key: `trip-${seg.tripId}-${index}`,
        tripNum: index + 1,
        startTime: seg.points[0]?.point_time,
        endTime: seg.points[seg.points.length - 1]?.point_time,
      });
      seg.points.forEach((p, pi) => {
        const ll = pointToLatLng(p);
        if (!ll) return;
        const kind = pi === 0 ? "start" : pi === seg.points.length - 1 ? "end" : "point";
        items.push({
          kind,
          key: `${kind}-${seg.tripId}-${pi}`,
          lat: ll.lat,
          lng: ll.lng,
          time: p?.point_time,
          tripNum: index + 1,
        });
      });
    });
    return items;
  }, [segments]);

  // Collapse all trips by default; user expands each trip as needed.
  useEffect(() => {
    setExpandedTrips({});
  }, [segments]);

  const clearMapOverlays = useCallback(() => {
    try {
      if (polylineRef.current?.remove) polylineRef.current.remove();
      if (startMarkerRef.current?.remove) startMarkerRef.current.remove();
      if (endMarkerRef.current?.remove) endMarkerRef.current.remove();
    } catch (_) {}
    polylineRef.current = null;
    startMarkerRef.current = null;
    endMarkerRef.current = null;
  }, []);

  const drawPathOnMap = useCallback(
    (path) => {
      const map = mapInstanceRef.current;
      if (!map || !path?.length) return;
      clearMapOverlays();

      try {
        if (DEBUG_DAY_PLAYBACK_FIT) {
          
        }

        if (path.length >= 2) {
          polylineRef.current = new window.mappls.Polyline({
            map,
            path,
            strokeColor: "#0000FF",
            strokeOpacity: 0.8,
            strokeWeight: 8,
            fitbounds: false,
          });
        }

        startMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: path[0].lat, lng: path[0].lng },
          icon: START_MARKER_ICON,
          width: 42,
          height: 42,
          fitbounds: false,
        });

        const end = path[path.length - 1];
        endMarkerRef.current = new window.mappls.Marker({
          map,
          position: { lat: end.lat, lng: end.lng },
          icon: END_MARKER_ICON,
          width: 42,
          height: 42,
          fitbounds: false,
        });
      } catch (err) {
      }

      // Default: fit the full path so entire day route is visible.
      fitViewport(map, path, timelineSheetOpen, { maxZoom: 14 });
    },
    [clearMapOverlays, timelineSheetOpen]
  );

  useEffect(() => { 
    combinedPointsRef.current = combinedPoints;
  }, [combinedPoints]);

  const openLatLngDetails = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      navigate(
        `${mapBasePath}/map/location?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`
      );
    },
    [mapBasePath, navigate]
  );

  const loadDayTrips = useCallback(
    async (dayKey) => {
      if (!deviceId) return;
      setIsLoading(true);
      try {
        const list = await get(
          `/trips?device_id=${encodeURIComponent(deviceId)}&page=1&per_page=500&sortBy=trip_start_time&sortOrder=desc`
        );
        const trips = normalizeTripsList(list);
        setAllTrips(trips);

        const filtered = trips
          .filter((tr) => {
            const d = parseTripDateLoose(tripStartDate(tr));
            return d ? toDateKey(d) === dayKey : false;
          })
          .sort((a, b) => {
            const t1 = parseTripDateLoose(tripStartDate(a))?.getTime() || 0;
            const t2 = parseTripDateLoose(tripStartDate(b))?.getTime() || 0;
            return t1 - t2;
          });
        setDayTrips(filtered);

        if (!filtered.length) {
          setSegments([]);
          setCombinedPoints([]);
          clearMapOverlays();
          return;
        }

        const details = await Promise.all(
          filtered.map(async (tr, index) => {
            const resolvedTripId = tr?.id || tr?._id || tr?.trip_id;
            const res = await get(`/trips/${resolvedTripId}`);
            const ptsRaw = Array.isArray(res?.points) ? res.points : [];
            const ptsSorted = [...ptsRaw].sort(
              (a, b) => new Date(a.point_time) - new Date(b.point_time)
            );
            return {
              tripId: resolvedTripId || `trip-${index + 1}`,
              trip: res?.trip || tr,
              points: ptsSorted,
            };
          })
        );

        const onlyWithPoints = details.filter((d) => d.points.length > 0);
        const joined = [];
        onlyWithPoints.forEach((d) => joined.push(...d.points));

        if (DEBUG_DAY_PLAYBACK_FIT) {
          
        }

        setSegments(onlyWithPoints);
        setCombinedPoints(joined);
      } catch (err) {
        toast.error(err?.message || "Failed to load day playback");
      } finally {
        setIsLoading(false);
      }
    },
    [clearMapOverlays, deviceId, get]
  );

  useEffect(() => {
    let cancelled = false;
    let raf = 0;

    const tryCreate = () => {
      if (cancelled || mapInstanceRef.current) return;
      const el = mapRef.current;
      if (!el) {
        raf = requestAnimationFrame(tryCreate);
        return;
      }

      const { width, height } = el.getBoundingClientRect();
      if (width < 8 || height < 8) {
        raf = requestAnimationFrame(tryCreate);
        return;
      }

      (async () => {
        try {
          await waitForMapplsSdk();
          if (cancelled || mapInstanceRef.current) return;
          const map = new window.mappls.Map(el, {
            center: DEFAULT_CENTER,
            zoom: 12,
            zoomControl: true,
            location: false,
            scrollWheel: true,
            draggable: true,
          });
          mapInstanceRef.current = map;

          const drawCurrent = () => {
            if (cancelled) return;
            mapLoadedRef.current = true;
            const path = (combinedPointsRef.current || []).map(pointToLatLng).filter(Boolean);
            if (DEBUG_DAY_PLAYBACK_FIT) {
              
            }
            if (path.length) {
              drawPathOnMap(path);
            }
          };

          if (typeof map.on === "function") {
            map.on("load", drawCurrent);
            setTimeout(() => {
              if (!mapLoadedRef.current) drawCurrent();
            }, 2000);
          } else {
            setTimeout(drawCurrent, 700);
          }
        } catch (e) {
          toast.error("Failed to initialize map");
        }
      })();
    };

    raf = requestAnimationFrame(tryCreate);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearMapOverlays();
    };
  }, [clearMapOverlays]);

  useEffect(() => {
    loadDayTrips(selectedDateKey);
  }, [loadDayTrips, selectedDateKey]);

  useEffect(() => {
    if (!combinedPoints.length || !mapInstanceRef.current) return;
    if (!mapLoadedRef.current) return;
    const path = combinedPoints.map(pointToLatLng).filter(Boolean);
    if (!path.length) return;
    const t = setTimeout(() => drawPathOnMap(path), 200);
    return () => clearTimeout(t);
  }, [combinedPoints, drawPathOnMap]);

  useEffect(() => {
    if (!timelineSheetOpen || !combinedPoints.length || !mapInstanceRef.current) return;
    const t = setTimeout(() => {
      const path = combinedPoints.map(pointToLatLng).filter(Boolean);
      if (path.length) fitViewport(mapInstanceRef.current, path, true, { maxZoom: 14 });
    }, 350);
    return () => clearTimeout(t);
  }, [timelineSheetOpen, combinedPoints]);

  useEffect(() => {
    if (!timelineSheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [timelineSheetOpen]);

  useEffect(() => {
    if (!timelineSheetOpen) return;
    const t = setTimeout(() => {
      try {
        if (typeof mapInstanceRef.current?.resize === "function") {
          mapInstanceRef.current.resize();
        }
      } catch (_) {}
    }, 320);
    return () => clearTimeout(t);
  }, [timelineSheetOpen]);

  const pageTitle = "Day Playback";

  return (
    <div className="relative flex h-screen w-full flex-col bg-background">
      <div className="flex h-14 shrink-0 items-center border-b text-foreground">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Day Playback
        </Button>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className={`relative min-h-0 w-full flex-1 overflow-hidden ${
            timelineSheetOpen || calendarSheetOpen ? "playback-hide-mappls-controls" : ""
          }`}
        >
          <style>{`
            .playback-hide-mappls-controls [class*="control"],
            .playback-hide-mappls-controls [class*="Control"],
            .playback-hide-mappls-controls [class*="zoom"],
            .playback-hide-mappls-controls [class*="Zoom"] {
              display: none !important;
              visibility: hidden !important;
              pointer-events: none !important;
            }
            /* Keep Mappls floating controls above the overlaid summary (matches min-h-[7.5rem] / sm:min-h-[8rem]) */
            #playback-map-container [style*="position: fixed"] {
              bottom: calc(8rem + env(safe-area-inset-bottom, 0px)) !important;
            }
          `}</style>

          <div className="absolute right-2 top-2 z-20 md:right-4 md:top-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTimelineSheetOpen(false);
                setCalendarSheetOpen(true);
              }}
              className="h-9 w-9 touch-manipulation border border-gray-200 bg-white p-0 text-gray-900 shadow-md hover:bg-white hover:text-gray-900 dark:bg-white"
              title="Select date"
              aria-label="Select date"
            >
              <CalendarDays className="h-4 w-4 text-gray-700" />
            </Button>
          </div>

          <div id="playback-map-container" ref={mapRef} className="h-full w-full min-h-[240px]" />

          {!isLoading && combinedPoints.length > 0 && (
            <button
              type="button"
              id="playback-timeline-summary"
              onClick={() => setTimelineSheetOpen(true)}
              className="group absolute bottom-0 left-0 right-0 z-30 flex min-h-[7.5rem] w-full min-w-0 items-center gap-4 rounded-t-[1.35rem] border border-border/60 bg-card/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3.5 text-left shadow-[0_-4px_24px_rgba(0,0,0,0.12)] backdrop-blur-md transition-[box-shadow,transform] hover:-translate-y-px hover:shadow-[0_-8px_28px_rgba(0,0,0,0.14)] dark:border-white/[0.08] dark:bg-card/90 dark:shadow-[0_-4px_28px_rgba(0,0,0,0.4)] sm:min-h-[8rem] sm:px-5 sm:pt-4"
              aria-label="Open day timeline"
            >
            <div
              className="pointer-events-none absolute bottom-3 left-0 top-3 w-[3px] rounded-full bg-gradient-to-b from-sky-500 to-emerald-500 opacity-80"
              aria-hidden
            />
            <div className="min-w-0 flex-1 pl-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium tracking-tight text-foreground">{pageTitle}</span>
                    <span className="text-muted-foreground/80">·</span>
                    <span className="text-xs text-muted-foreground">
                      {dayTrips.length} trip{dayTrips.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                {/* Visual CTA only (card click opens the timeline sheet). */}
                <div className="shrink-0 flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-2 py-1.5 text-muted-foreground transition-colors group-hover:border-sky-400/30 group-hover:text-foreground">
                  <ChevronUp className="h-4 w-4" aria-hidden />
                  <span className="text-[10px] font-medium tracking-wide uppercase">Timeline</span>
                </div>
              </div>

              <p className="mt-0.5 text-xs text-muted-foreground">
                {selectedCalendarDate.toLocaleDateString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>

              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums text-foreground/90">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {summary.runtimeLabel}
                </span>
                <span className="h-3 w-px bg-border/80" aria-hidden />
                <span className="inline-flex items-center gap-1">
                  <Navigation className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {summary.distanceLabel}
                </span>
                <span className="h-3 w-px bg-border/80" aria-hidden />
                <span className="inline-flex items-center gap-1">
                  <Gauge className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {summary.avgLabel}
                </span>
              </div>
            </div>
            </button>
          )}
        </div>

        {timelineSheetOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/50"
              aria-label="Close timeline"
              onClick={() => setTimelineSheetOpen(false)}
            />
            <div
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[76vh] flex-col overflow-hidden rounded-t-2xl border-1 border-muted-foreground/20 bg-background shadow-2xl dark:border-muted-foreground/15 dark:bg-[#181818]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="day-playback-timeline-title"
            >
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-24 rounded-t-2xl bg-gradient-to-b from-sky-500/18 via-primary/8 to-transparent dark:from-sky-400/10 dark:via-primary/5"
                aria-hidden
              />
              <div className="relative shrink-0 px-3 pt-3" aria-hidden>
                <div className="mx-auto h-1 w-10 rounded-full bg-muted-foreground/35 dark:bg-muted-foreground/25" />
              </div>
              <div className="relative flex shrink-0 items-center justify-between gap-2 px-3 pb-3 pt-2">
                <div className="min-w-0 flex-1 rounded-xl border-1 border-muted-foreground/20 bg-card px-3 py-2.5 shadow-md ring-1 ring-black/[0.06] dark:border-white/10 dark:bg-card/70 dark:shadow-sm dark:ring-white/10">
                  <h2 id="day-playback-timeline-title" className="truncate text-base font-semibold text-foreground">
                    Day timeline
                  </h2>
                  <p className="truncate text-xs text-muted-foreground">
                    {selectedCalendarDate.toLocaleDateString(undefined, {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    · {dayTrips.length} trip{dayTrips.length === 1 ? "" : "s"}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 rounded-full border-1 border-muted-foreground/20 bg-card shadow-md ring-1 ring-black/[0.04] hover:bg-muted dark:border-white/10 dark:bg-background/80 dark:ring-white/5 dark:hover:bg-muted"
                  onClick={() => setTimelineSheetOpen(false)}
                  aria-label="Close"
                >
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                </Button>
              </div>
              <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-6 pt-1 ">
                {timelineItems.length === 0 && (
                  <div className="rounded-2xl border-1 border-muted-foreground/20 border-border bg-muted/30 px-4 py-10 text-center dark:border-muted-foreground/25 dark:bg-muted/20">
                    <Route className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" aria-hidden />
                    <p className="text-sm font-medium text-foreground">No trips for this day</p>
                    <p className="mt-1 text-xs text-muted-foreground">Pick another date on the calendar.</p>
                  </div>
                )}
                {timelineItems.length > 0 && (
                  <div className="space-y-5">
                    <div className="rounded-2xl border-1 border-muted-foreground/20 bg-gradient-to-br from-card via-card to-sky-500/[0.07] p-4 shadow-md ring-1 ring-black/[0.06] dark:from-card dark:to-emerald-950/20 dark:ring-white/10">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full w-full rounded-full bg-gradient-to-r from-sky-500 via-primary to-emerald-500 opacity-90" />
                      </div>
                      <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl border-1 border-muted-foreground/20 bg-muted/40 px-2 py-3 text-center dark:border-white/10 dark:bg-background/30">
                        <div className="flex flex-col items-center gap-1">
                          <Clock className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            Duration
                          </span>
                          <span className="text-xs font-semibold tabular-nums text-foreground">
                            {summary.runtimeLabel}
                          </span>
                        </div>
                        <div className="flex flex-col items-center gap-1 border-x border-border/60">
                          <Navigation className="h-4 w-4 text-primary" />
                          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            Distance
                          </span>
                          <span className="text-xs font-semibold tabular-nums text-foreground">
                            {summary.distanceLabel}
                          </span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <Gauge className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            Avg / trip
                          </span>
                          <span className="text-xs font-semibold tabular-nums text-foreground">
                            {summary.avgLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="mb-3 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Trips on map
                      </p>
                      <div className="relative mx-auto max-w-lg pb-4">
                        <div
                          className="absolute bottom-8 left-[15px] top-8 w-1 rounded-full  bg-gradient-to-b from-sky-500 via-primary/70 to-emerald-500 opacity-90 shadow-sm"
                          aria-hidden
                        />
                        <ul className="relative space-y-0">
                          {segments.map((seg, segIdx) => {
                            const isExpanded = Boolean(expandedTrips[seg.tripId]);
                            const tripNum = segIdx + 1;
                            const startTime = seg.points[0]?.point_time;
                            const endTime = seg.points[seg.points.length - 1]?.point_time;

                            return (
                              <li key={`seg-${seg.tripId}-${segIdx}`} className="relative pb-3 pl-10">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedTrips((prev) => ({ ...prev, [seg.tripId]: !prev[seg.tripId] }))
                                  }
                                  className="group mb-2 flex w-full items-center justify-between gap-3 rounded-xl border-1 border-muted-foreground/20 bg-gradient-to-r from-card to-muted/40 px-3 py-3 text-left shadow-md ring-1 ring-black/[0.05] transition-all hover:border-sky-400/50 hover:shadow-lg dark:from-card/80 dark:to-muted/20 dark:ring-white/5 dark:hover:border-primary/40"
                                  aria-expanded={isExpanded}
                                  aria-label={`Toggle trip ${tripNum}`}
                                >
                                  <div className="flex min-w-0 items-start gap-2.5 ">
                                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500/20 to-emerald-500/15 text-sky-700 ring-1 ring-sky-500/25 dark:text-sky-300 dark:ring-sky-400/30">
                                      <Route className="h-4 w-4" aria-hidden />
                                    </span>
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ">
                                        Trip {tripNum}
                                      </p>
                                      <p className="mt-0.5 text-sm font-medium text-foreground">
                                        {formatTimeAmPm(startTime)} → {formatTimeAmPm(endTime)}
                                      </p>
                                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                                        {seg.points.length} point{seg.points.length === 1 ? "" : "s"}
                                      </p>
                                    </div>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition group-hover:text-foreground " />
                                  )}
                                </button>

                                <div
                                  className={`grid transition-all  duration-300 ease-in-out ${
                                    isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                  }`}
                                >
                                  <div className="overflow-hidden ">
                                    {seg.points.map((p, pi) => {
                                      const ll = pointToLatLng(p);
                                      if (!ll) return null;
                                      const kind =
                                        pi === 0 ? "start" : pi === seg.points.length - 1 ? "end" : "point";
                                      const label =
                                        kind === "start"
                                          ? "Trip started"
                                          : kind === "end"
                                            ? "Trip ended"
                                            : "Waypoint";

                                      return (
                                        <div
                                          key={`${seg.tripId}-${pi}`}
                                          className="relative pb-6 pl-0 last:pb-2"
                                        >
                                          <div
                                            className={`absolute top-1.5 ${
                                              kind === "start" || kind === "end"
                                                ? "-left-10 flex h-8 w-8 items-center justify-center rounded-full border-1 border-muted-foreground/20 shadow-md ring-2 ring-background"
                                                : "-left-[27px] h-4 w-4 rounded-full border-2 border-background shadow-sm"
                                            } ${
                                              kind === "start"
                                                ? "border-sky-200 bg-gradient-to-br from-sky-400 to-sky-600"
                                                : kind === "end"
                                                  ? "border-emerald-200 bg-gradient-to-br from-emerald-500 to-emerald-700"
                                                  : "bg-muted ring-2 ring-muted-foreground/25"
                                            }`}
                                            aria-hidden
                                          >
                                            {kind === "end" ? (
                                              <MapPin className="h-4 w-4 text-white" />
                                            ) : null}
                                          </div>
                                          <div className="rounded-xl border-1 border-muted-foreground/20 bg-background p-3 shadow-sm dark:border-white/10 dark:bg-background/40">
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="min-w-0">
                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                  {label}
                                                </p>
                                                <p className="mt-1 font-mono text-sm font-semibold leading-snug text-foreground">
                                                  {formatCoord(ll.lat, ll.lng)}
                                                </p>
                                                <p className="mt-1.5 text-xs text-muted-foreground">
                                                  {formatTimeAmPm(p?.point_time)}
                                                  <span className="text-border"> · </span>
                                                  Trip {tripNum}
                                                </p>
                                              </div>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="shrink-0 rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary"
                                                onClick={() => openLatLngDetails(ll.lat, ll.lng)}
                                                aria-label="Show location"
                                              >
                                                <ChevronRight className="h-5 w-5" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <Sheet open={calendarSheetOpen} onOpenChange={setCalendarSheetOpen}>
          <SheetContent side="bottom" className="border-t border-border bg-background p-0">
            <div className="px-4 pb-4 pt-3">
              <SheetHeader className="px-0">
                <SheetTitle className="text-base">Select Date</SheetTitle>
                <SheetDescription>Choose a day to load all trip paths in one map.</SheetDescription>
              </SheetHeader>
              <div className="mt-3 rounded-2xl border bg-card p-2">
                <CalendarPicker
                  mode="single"
                  selected={selectedCalendarDate}
                  components={{
                    DayButton: (btnProps) => {
                      const date = btnProps?.day?.date;
                      const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
                      const key = d ? toDateKey(d) : null;
                      const km = key ? distanceByCalendarDay.get(key) : null;
                      return (
                        <button {...btnProps}>
                          <span className="flex h-9 w-9 flex-col items-center justify-center leading-none">
                            <span className="text-[12px]">{d ? d.getDate() : ""}</span>
                            {typeof km === "number" && Number.isFinite(km) && km > 0 ? (
                              <span className="mt-0.5 text-[9px] font-semibold text-muted-foreground">
                                {km.toFixed(km < 10 ? 1 : 0)}km
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    },
                  }}
                  onSelect={(d) => {
                    if (!d) return;
                    const key = toDateKey(d);
                    setSelectedDateKey(key);
                    navigate(`${mapBasePath}/map/day-playback/${encodeURIComponent(deviceId)}?date=${key}`, {
                      replace: true,
                    });
                    setCalendarSheetOpen(false);
                  }}
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 top-14 flex items-center justify-center bg-background/40">
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      )}
    </div>
  );
}
